import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Inject, Input,
  OnChanges, OnDestroy, OnInit, Output, PLATFORM_ID, QueryList, Renderer2, SimpleChanges, TemplateRef,
  ViewChildren, ViewEncapsulation
} from '@angular/core';
import { isPlatformBrowser, NgTemplateOutlet, SlicePipe } from '@angular/common';

import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import {
  BrandsEnum, DeviceService, expandCollapseAnimation, ISliderInfo, PositionEnum, SdButtonComponent, SizesEnum
} from '@seb/web-design-system/core';

import { DynamicContentShowMore } from './show-more.interface';

@Component({
  selector: 'sd-show-more',
  templateUrl: './show-more.component.html',
  animations: [
    expandCollapseAnimation({
      anchor: 'expandCollapse',
      durationIn: 250,
      delayIn: 0,
      durationOut: 250,
      delayOut: 0
    })
  ],
  styleUrls: ['./show-more.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgTemplateOutlet,
    SdButtonComponent,
    SlicePipe,
    TranslateModule
  ]
})
export class SdShowMoreComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  brand: BrandsEnum;

  @Input()
  isLazyLoading = false;

  @Input()
  showButton: boolean;

  @Input()
  showedRow = 1;

  @Input()
  size = SizesEnum.s;

  @Input()
  marginSize: SizesEnum;

  @Input()
  showSize = 1;

  @Input()
  items: any[];

  @Input()
  totalCount: number;

  @Input()
  showLessButton = false;

  @Input()
  context: string;

  @Input()
  customButtonTitle: string;

  @Input()
  isList = false;

  @Input()
  equalHeight = false;

  @Input()
  layoutTextConfig:
    | PositionEnum.left
    | PositionEnum.right
    | PositionEnum.center = PositionEnum.center;

  @Input()
  dynamicContentShowMore: DynamicContentShowMore;

  @ViewChildren('itemsRow')
  itemsRow: QueryList<ElementRef>;

  @ViewChildren('itemsElement')
  itemsElement: QueryList<ElementRef>;

  @Input()
  itemTemplate: TemplateRef<any>;

  @Output()
  more = new EventEmitter<ISliderInfo>();

  @Output()
  less = new EventEmitter<ISliderInfo>();

  @Input() class = '';
  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.size ? 'is-size-' + this.size : '',
      this.marginSize ? 'has-margin-' + this.marginSize : '',
      this.equalHeight ? 'are-equals' : '',
      this.isList,
      this.isList ? 'is-list' : '',
      this.layoutTextConfig
        ? 'is-layout-' + this.layoutTextConfig
        : 'is-layout-' + PositionEnum.center
    ]
      .filter(Boolean)
      .join(' ');
  }

  rowNumber: number;
  arrRows;
  itemsPerRow = 3;
  tabletSubscription: Subscription;
  mobileSubscription: Subscription;
  isTablet$: boolean;
  isMobile$: boolean;
  _showedRow: number;

  constructor(
    private cdRef: ChangeDetectorRef,
    private renderer: Renderer2,
    @Inject(PLATFORM_ID) private platformId: any,
    private deviceService: DeviceService,
  ) {}

  ngOnInit() {
    this.tabletSubscription = this.deviceService.isTablet.subscribe((x) => {
      this.isTablet$ = x;
      this.setRows();
      this.cdRef.markForCheck();
    });
    this.mobileSubscription = this.deviceService.isMobile.subscribe((x) => {
      this.isMobile$ = x;
      this.setRows();
      this.cdRef.markForCheck();
    });
    this.setRows();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (isPlatformBrowser(this.platformId)) {
      if (changes.size || changes.items || changes.showedRow) {
        this.setRows();
      }
    }
  }

  ngOnDestroy() {
    this.mobileSubscription?.unsubscribe();
    this.tabletSubscription?.unsubscribe();
  }

  setRows() {
    switch (this.size) {
      case SizesEnum.l: {
        this.itemsPerRow = 1;
        break;
      }
      case SizesEnum.m: {
        this.itemsPerRow = this.isTablet$ ? 2 : this.isMobile$ ? 1 : 2;
        break;
      }
      case SizesEnum.s: {
        this.itemsPerRow = this.isTablet$ ? 2 : this.isMobile$ ? 1 : 3;
        break;
      }
      case SizesEnum.xs: {
        this.itemsPerRow = this.isTablet$ ? 2 : this.isMobile$ ? 1 : 4;
        break;
      }
      case SizesEnum.xxs: {
        this.itemsPerRow = this.isTablet$ ? 3 : this.isMobile$ ? 2 : 6;
        break;
      }
    }
    this.rowNumber = Math.ceil(this.items?.length / this.itemsPerRow);
    if (this.items?.length && this._showedRow >= this.rowNumber) {
      this._showedRow = this.rowNumber;
    } else {
      this._showedRow = this.showedRow;
    }
    this.arrRows = Array(this._showedRow).fill(0);
  }

  manageShowMore() {
    if (this.isLazyLoading) {
      this.more.emit();
    } else {
      this.showMore();
    }
  }

  showMore() {
    if (this._showedRow + this.showSize < this.rowNumber) {
      this._showedRow = this._showedRow + this.showSize;
      this.showedRow = this._showedRow;
    } else {
      this._showedRow = this.rowNumber;
      this.showedRow = this.rowNumber;
    }

    this.more.emit({
      index: this._showedRow - 1,
      itemsPerView: this.itemsPerRow
    });

    this.arrRows = Array(this._showedRow).fill(0);

    /* A11Y */
    setTimeout(() => {
      const firstElOfRowsOfItems = this.itemsRow.toArray()[this._showedRow - this.showSize].nativeElement.querySelector('.c__item');
      this.renderer.setAttribute(firstElOfRowsOfItems, 'tabindex', '-1');
      firstElOfRowsOfItems.focus();
    }, 250);
  }

  showLess() {
    this._showedRow = 1;
    this.less.emit({ index: 0, itemsPerView: this.itemsPerRow });
    /* A11Y */
    setTimeout(() => {
      const firstElOfRowsOfItems = this.itemsRow.toArray()[this._showedRow - 1].nativeElement.querySelector('.c__item');
      this.renderer.setAttribute(firstElOfRowsOfItems, 'tabindex', '-1');
      firstElOfRowsOfItems.focus();
    }, 250);
  }
}
